/**
 * Pre-Launch Page for trAInedup.ai
 * Holding page with newsletter signup for pre-launch visitors
 */

import React, { useState } from 'react';
import { Layout } from '../components/common/Layout';
import { useTranslation } from 'react-i18next';
import { 
  Mail, 
  CheckCircle, 
  ArrowRight, 
  Sparkles, 
  Brain, 
  Target, 
  Users,
  Zap,
  BookOpen,
  TrendingUp,
  Database
} from 'lucide-react';
import { Button, ButtonLink } from '../components/common/Button';
import { Input } from '../components/common/Input';
import { Card } from '../components/common/Card';
import { newsletterService, AVAILABLE_EBOOKS } from '../services/firebase/newsletter';

/**
 * Pre-launch page component
 */
export const PreLaunchPage: React.FC = () => {
  const { t } = useTranslation('prelaunch');
  const [email, setEmail] = useState('');
  const [selectedEbook, setSelectedEbook] = useState('');
  const [honeypot, setHoneypot] = useState(''); // Bot detection
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [downloadUrl, setDownloadUrl] = useState('');
  const [error, setError] = useState('');
  const videoUrl = 'https://firebasestorage.googleapis.com/v0/b/trainedup-a4915.firebasestorage.app/o/Promo%2FPromo%20video%20home%20page.mp4?alt=media&token=f0e9e512-5797-45e8-941f-43db962a229a';

  // Ref for video element
  const videoRef = React.useRef<HTMLVideoElement>(null);

  React.useEffect(() => {
    const videoEl = videoRef.current;
    if (!videoEl) return;

    // Intersection Observer callback
    // threshold: 0.5 means the callback fires when 50% of the video is visible in the viewport
    const handleIntersection = (entries: IntersectionObserverEntry[]) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          videoEl.play().catch(() => {});
        } else {
          videoEl.pause();
        }
      });
    };

    const observer = new window.IntersectionObserver(handleIntersection, {
      threshold: 0.5 // Play/pause when 50% visible/invisible
    });
    observer.observe(videoEl);

    return () => {
      observer.disconnect();
    };
  }, []);

  /**
   * Handle newsletter signup
   */  const handleSignup = async (e: React.FormEvent) => {
    e.preventDefault();

    // Honeypot check - if filled, it's likely a bot
    if (honeypot) {
      // Bot detected via honeypot field
      setError('Please try again.');
      return;
    }

    if (!email.trim()) {
      setError(t('errorEmailRequired'));
      return;
    }

    if (!email.includes('@') || !email.includes('.')) {
      setError(t('errorEmailInvalid'));
      return;
    }

    if (!selectedEbook) {
      setError(t('errorEbookRequired'));
      return;
    }

    setIsSubmitting(true);
    setError('');

    try {
      const result = await newsletterService.addSubscriber(
        email.trim(), 
        'pre-launch-page', 
        selectedEbook
      );
      setIsSubmitted(true);
      setEmail('');
      setSelectedEbook('');
      if (result.downloadUrl) {
        setDownloadUrl(result.downloadUrl);
      }
    } catch (error: any) {
      setError(error.message || 'Failed to sign up. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  /**
   * Feature card component
   */
  const FeatureCard: React.FC<{
    icon: React.ComponentType<any>;
    title: string;
    description: string;
    color: string;
  }> = ({ icon: Icon, title, description, color }) => (
    <Card className="p-6 text-center hover:shadow-lg transition-shadow duration-300">
      <div className={`w-12 h-12 ${color} rounded-lg flex items-center justify-center mx-auto mb-2`}>
        <Icon className="w-6 h-6 text-white" />
      </div>
      <h3 className="font-semibold text-sm text-neutral-900 mb-2">{title}</h3>
      <p className="text-neutral-600 text-sm">{description}</p>
    </Card>
  );

  // Icon mapping for ebooks
  const ebookIcons: Record<string, React.ComponentType<any>> = {
    adcreative: Zap,
    prompts: Brain,
    gretel: Database, // changed from Users to Database
    jasper: BookOpen,
    turnitin: TrendingUp,
  };

  return (
    <Layout showHeader showFooter>
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50">
      {/* Header */}
      <header className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-primary/10 to-purple-600/10"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          {/* Logo */}
          <div className="flex items-center justify-center mb-8">
             <img src="tulogo.png" alt="tAI Logo" className="h-32 w-auto" />
            {/* <div className="w-12 h-12 bg-primary rounded-xl flex items-center justify-center mr-3">
              <span className="text-white font-bold text-lg">tAI</span>
            </div>
            <span className="font-montserrat font-bold text-2xl text-neutral-800">
              trAInedup.ai
            </span> */}
          </div>

          {/* Hero Section */}
          <div className="text-center max-w-4xl mx-auto">
            <div className="flex items-center justify-center mb-6">
            <div className="flex items-center space-x-2 bg-primary/10 text-primary px-4 py-2 rounded-full text-sm font-medium">
              <Sparkles className="w-4 h-4" />
              <span>{t('comingSoon')}</span>
            </div>
            </div>

            <h1 className="font-montserrat font-bold text-4xl md:text-6xl text-neutral-900 mb-6">
              {t('heroTitle1')}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary to-purple-600">
                {' '}{t('heroTitle2')}
              </span>
            </h1>

            <p className="text-xl text-neutral-600 mb-8 max-w-3xl mx-auto leading-relaxed">
              {t('heroDesc')}
            </p>

            {/* Ebook Selection - now full width */}
            <div className="space-y-3 mb-8">
              <div className="block text-lg font-semibold text-primary mb-1">
                {t('emailPrompt')}
              </div>
              <div className="text-neutral-700 text-base mb-2">
                {t('ebookThankYou')}
              </div>
              <div className="block text-sm font-medium text-neutral-700 mb-2">
                {t('selectEbook')}
              </div>
              <div className="max-w-4xl mx-auto w-full">
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6 items-stretch">
                  {AVAILABLE_EBOOKS.map((ebook) => {
                    const Icon = ebookIcons[ebook.id] || BookOpen;
                    const selected = selectedEbook === ebook.id;
                    return (
                      <label
                        key={ebook.id}
                        className={`flex flex-col items-center justify-between w-full min-h-[8rem] p-4 border rounded-xl cursor-pointer transition-all shadow-sm
                          ${selected ? 'bg-primary border-primary ring-2 ring-primary' : 'border-neutral-200 bg-white hover:border-primary/50'}
                        `}
                        style={{ height: '100%' }}
                      >
                        <input
                          type="radio"
                          name="ebook"
                          value={ebook.id}
                          checked={selected}
                          onChange={(e) => setSelectedEbook(e.target.value)}
                          className="sr-only"
                          disabled={isSubmitting}
                        />
                        <div className={`w-12 h-12 flex items-center justify-center mb-2`}>
                          <Icon className={`w-7 h-7 ${selected ? 'text-white' : 'text-primary'}`} />
                        </div>
                        <div className={`font-semibold text-center text-sm mb-1 ${selected ? 'text-white' : 'text-neutral-900'}`}>
                          {t(`ebooks.${ebook.id}.title`)}
                        </div>
                        <div className={`text-xs text-center ${selected ? 'text-blue-100' : 'text-neutral-600'}`}>
                          {t(`ebooks.${ebook.id}.desc`)}
                        </div>
                      </label>
                    );
                  })}
                </div>
              </div>
            </div>

            {/* Newsletter Signup */}
            <div className="max-w-md mx-auto mb-12">
              {!isSubmitted ? (
                <form onSubmit={handleSignup} className="space-y-4">
                  {/* Honeypot field - hidden from users but visible to bots */}
                  <input
                    type="text"
                    name="website"
                    value={honeypot}
                    onChange={(e) => setHoneypot(e.target.value)}
                    style={{ 
                      position: 'absolute', 
                      left: '-9999px', 
                      width: '1px', 
                      height: '1px',
                      opacity: 0,
                      overflow: 'hidden'
                    }}
                    tabIndex={-1}
                    autoComplete="off"
                  />
                  <div className="flex flex-col sm:flex-row gap-3">
                    <Input
                      type="email"
                      placeholder={t('emailPlaceholder')}
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="flex-1"
                      disabled={isSubmitting}
                    />
                    <Button
                      type="submit"
                      variant="primary"
                      icon={ArrowRight}
                      loading={isSubmitting}
                      className="sm:w-auto"
                    >
                      {t('getNotified')}
                    </Button>
                  </div>
                  {error && (
                    <p className="text-red-600 text-sm text-left">{error}</p>
                  )}
                  <p className="text-neutral-500 text-sm">
                    {t('noSpam')}
                  </p>
                </form>
              ) : (
                <div className="text-center p-6 bg-green-50 rounded-lg border border-green-200">
                  <CheckCircle className="w-12 h-12 text-green-600 mx-auto mb-4" />
                  <h3 className="font-semibold text-green-900 mb-2">{t('onList')}</h3>
                  <p className="text-green-700 text-sm mb-4">
                    {t('notifyWhenReady')}
                  </p>
                  {downloadUrl ? (
                    <div className="mt-4 p-4 bg-white rounded-lg border border-green-300">
                      <h4 className="font-medium text-green-900 mb-2">{t('ebookReady')}</h4>
                      <ButtonLink
                        href={downloadUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        variant="primary"
                        className="w-full sm:w-auto"
                      >
                        {t('downloadEbook')}
                      </ButtonLink>
                      <p className="text-green-600 text-xs mt-2">
                        {/* Download link is valid for 24 hours */}
                      </p>
                    </div>
                  ) : selectedEbook ? (
                    <div className="mt-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
                      <h4 className="font-medium text-blue-900 mb-2">{t('ebookPreparing')}</h4>
                      <p className="text-blue-700 text-sm">
                        {t('ebookPreparingDesc', { ebook: AVAILABLE_EBOOKS.find(e => e.id === selectedEbook)?.title })}
                      </p>
                    </div>
                  ) : null}
                </div>
              )}
            </div>

            {/* Social Proof */}
            <div className="flex items-center justify-center space-x-8 text-neutral-500 text-sm">
              <div className="flex items-center space-x-2">
                <Users className="w-4 h-4" />
                <span>{t('socialProofSubscribers')}</span>
              </div>
              <div className="flex items-center space-x-2">
                <Zap className="w-4 h-4" />
                <span>{t('socialProofLaunch')}</span>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* What We're Building */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="font-montserrat font-bold text-3xl text-neutral-900 mb-4">
              {t('whatWereBuildingTitle')}
            </h2>
            <p className="text-lg text-neutral-600 max-w-3xl mx-auto">
              {t('whatWereBuildingDesc')}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              { icon: Brain, color: 'bg-blue-500' },
              { icon: Target, color: 'bg-green-500' },
              { icon: BookOpen, color: 'bg-purple-500' },
              { icon: Users, color: 'bg-orange-500' },
              { icon: TrendingUp, color: 'bg-teal-500' },
              { icon: Zap, color: 'bg-red-500' },
            ].map((item, idx) => (
              <FeatureCard
                key={idx}
                icon={item.icon}
                title={t(`featureCards.${idx}.title`)}
                description={t(`featureCards.${idx}.desc`)}
                color={item.color}
              />
            ))}
          </div>
        </div>
      </section>
      <section className="pt-0 pb-1 bg-transparent">
        <div className="px-2 py-0 m-0 aspect-video bg-transparent rounded-lg overflow-hidden relative">
          <video
            ref={videoRef}
            className="w-full h-full"
            controls
            preload="auto"
            src={videoUrl}
            poster="/tulogo.png"
            playsInline
          >
            Your browser does not support the video tag.
          </video>
        </div>
</section>

      {/* Why trAInedup.ai */}
      <section className="py-16 bg-transparent">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="font-montserrat font-bold text-3xl text-neutral-900 mb-6">
                {t('whyTitle')}
              </h2>
              <div className="space-y-6">
                {Array.from({ length: 4 }).map((_, idx) => (
                  <div className="flex items-start space-x-4" key={idx}>
                    <div className="w-8 h-8 bg-primary rounded-lg flex items-center justify-center flex-shrink-0 mt-1">
                      <CheckCircle className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-neutral-900 mb-2">{t(`whyReasons.${idx}.title`)}</h3>
                      <p className="text-neutral-600">
                        {t(`whyReasons.${idx}.desc`)}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            
            <div className="relative">
              <div className="aspect-square bg-gradient-to-br from-primary/20 to-purple-600/20 rounded-2xl flex items-center justify-center">
                <div className="p-8 bg-white rounded-2xl flex items-center justify-center">
                  <img src="U to the power of AI.png" alt="tAI Logo" className="h-auto w-auto rounded-xl" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-r from-primary to-purple-600">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-montserrat font-bold text-3xl text-white mb-4">
            {t('ctaTitle')}
          </h2>
          <p className="text-xl text-bold text-blue-100 mb-8">
            {t('ctaGuided')}
          </p>
          <p className="text-xl text-blue-100 mb-8">
            {t('ctaJoin')}
          </p>

          {/* Ebook Selection for CTA - purple background variant */}
          <div className="space-y-3 mb-8">
            <div className="block text-lg font-semibold text-white mb-1">
              {t('ctaEmailPrompt')}
            </div>
            <div className="text-blue-100 text-base mb-2">
              {t('ctaThankYou')}
            </div>
            <div className="block text-sm font-medium text-blue-100 mb-2">
              {t('ctaSelectEbook')}
            </div>
            <div className="max-w-4xl mx-auto w-full">
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6 items-stretch">
                {AVAILABLE_EBOOKS.map((ebook) => {
                  const Icon = ebookIcons[ebook.id] || BookOpen;
                  const selected = selectedEbook === ebook.id;
                  return (
                    <label
                      key={ebook.id}
                      className={`flex flex-col items-center justify-between w-full min-h-[8rem] p-4 border rounded-xl cursor-pointer transition-all shadow-sm
                        ${selected ? 'bg-white border-white ring-2 ring-white' : 'border-blue-200 bg-primary/30 hover:border-white/70'}
                      `}
                      style={{ height: '100%' }}
                    >
                      <input
                        type="radio"
                        name="ebook-cta"
                        value={ebook.id}
                        checked={selected}
                        onChange={(e) => setSelectedEbook(e.target.value)}
                        className="sr-only"
                        disabled={isSubmitting}
                      />
                      <div className={`w-12 h-12 flex items-center justify-center mb-2`}>
                        <Icon className={`w-7 h-7 ${selected ? 'text-primary' : 'text-white'}`} />
                      </div>
                      <div className={`font-semibold text-center text-sm mb-1 ${selected ? 'text-primary' : 'text-white'}`}>
                        {t(`ebooks.${ebook.id}.title`)}
                      </div>
                      <div className={`text-xs text-center ${selected ? 'text-primary' : 'text-blue-100'}`}>
                        {t(`ebooks.${ebook.id}.desc`)}
                      </div>
                    </label>
                  );
                })}
              </div>
            </div>
          </div>

          {!isSubmitted && (
            <div className="max-w-md mx-auto">
              <form onSubmit={handleSignup} className="space-y-4">
                <div className="flex flex-col sm:flex-row gap-3">
                  <Input
                    type="email"
                    placeholder={t('emailPlaceholder')}
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="flex-1 bg-white"
                    disabled={isSubmitting}
                  />
                  <Button
                    type="submit"
                    variant="secondary"
                    icon={Mail}
                    loading={isSubmitting}
                    className="sm:w-auto bg-primary text-white hover:bg-primary/90 border border-white"
                  >
                    {t('ctaJoinWaitlist')}
                  </Button>
                </div>
                {error && (
                  <p className="text-red-200 text-sm text-left">{error}</p>
                )}
              </form>
            </div>
          )}
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 bg-neutral-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row items-center justify-between">
            <div className="flex items-center mb-4 md:mb-0">
              <div className="w-8 h-8 bg-white rounded-lg flex items-center justify-center mr-2">
                 <img src="tulogo.png" alt="tAI Logo" className="h-8 w-auto" />
              </div>
              <span className="font-montserrat font-bold text-lg text-white">
                trAInedup.ai
              </span>
            </div>
            
            <div className="text-neutral-400 text-sm">
              {t('footerCopyright')}
            </div>
          </div>
        </div>
      </footer>
      </div>
    </Layout>
  );
};

